import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { GroupNotesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as GroupActions from "../../../actions/groups";
import * as NavigationActions from "../../../actions/navigation";
import { RestrictedLayout } from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { GroupSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./GroupNotesPage.scss";

interface GroupNotePageProps {
    group: Dtos.Group;
    loadingGroup: boolean;
    loadGroupSuccess: boolean;
    loadGroupFailure: boolean;
    loadGroupErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearGroup: GroupActions.IGroupClearActionCreator;
    loadGroup: GroupActions.IGroupLoadByIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const GroupNotePage: FC<GroupNotePageProps> = ({
    group,
    loadingGroup,
    loadGroupSuccess,
    loadGroupFailure,
    loadGroupErrorCode,
    permissions,
    clearGroup,
    loadGroup,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { groupId } = useParams<{ groupId: string }>();
    const groupIdNumber = parseInt(groupId, 10);

    //
    // -- States --
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showGroupNotes = !loadingGroup && loadGroupSuccess && !creatingTree;


    // Load group
    useEffect(() => {
        if (groupIdNumber && !group) {
            setDrawerItems(GroupSubMenu(groupIdNumber, permissions));
            setBreadcrumbs(GroupNotesBreadcrumbs(groupIdNumber));

            setTreeData(null);

            loadGroup(groupIdNumber);
        }
    }, [
        groupIdNumber,
        group,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        loadGroup,
        setTreeData,
        GroupSubMenu,
        GroupNotesBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadGroupFailure) {
            navigate(`/error/${loadGroupErrorCode}`);
            clearGroup();
        }
    }, [loadGroupFailure, loadGroupErrorCode, navigate, clearGroup]);

    // Update breadcrumbs
    useEffect(() => {
        if (groupIdNumber && group?.name) {
            setBreadcrumbs(GroupNotesBreadcrumbs(groupIdNumber, group.name));
        }
    }, [
        group,
        groupIdNumber,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        GroupNotesBreadcrumbs,
    ]);

    // Cleanup effect to clear the group on component unmount
    useEffect(() => {
        return () => {
            setTreeData(null);
            clearGroup();
        };
    }, [clearGroup, setTreeData]);

    // Update tree
    useEffect(() => {
        setInitalizedTreeData(false);
    }, [group, setInitalizedTreeData]);

    // Create tree
    useEffect(() => {
        if (group && !initalizedTreeData) {
            setCreatingTree(true);

            // If no group notes return
            if (!group?.notes || group?.notes?.length == 0) {
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (notes) => {
                let children = [];

                children =
                    notes
                        ?.sort((a, b) => a?.id - b?.id)
                        ?.map((y) => ({
                            id: `note-${y.id}`,
                            data: (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: " 1fr 3fr 8fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={FontAwesomeIcons.Duotone.NOTE}
                                        />
                                    </div>
                                    <div className="px-1">{y?.title}</div>
                                    <div className="d-inline-block px-1">
                                        <span
                                            className="text-truncate"
                                            style={{ whiteSpace: "pre-line" }}
                                        >
                                            {y?.comments}
                                        </span>
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 8fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Title</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Comment</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                group.notes
                    ?.sort((a, b) =>
                        a?.noteType?.value.localeCompare(b?.noteType?.value)
                    )
                    ?.map((x) => ({
                        id: `type-${x.noteTypeId}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Duotone.NOTESMEDICAL}
                                />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "180px" }}
                                >
                                    {x.noteType?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary mb-0">
                                        {x?.notes?.length}
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createChildNodes(x.notes)],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        group,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("note") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.GroupNotesView
                    )
                ) {
                    navigate(
                        `/groups/${groupIdNumber}/notes/${id.replace(
                            "note-",
                            ""
                        )}`
                    );
                }
                if (id.includes("type")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, groupIdNumber, permissions, group]
    );

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-9 mb-2"}>
                        <h1>Group Notes</h1>
                    </div>
                    <div className="col-3" style={{ textAlign: "right" }}>
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.GroupNotesCreate
                        ) &&
                            group &&
                            group.id > 0 && (
                                <Link
                                    className={"btn btn-primary"}
                                    url={`/groups/${group.id}/notes/create`}
                                >
                                    Create
                                </Link>
                            )}
                    </div>
                </div>
                {!showGroupNotes ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showGroupNotes ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>
                                        There are currently no group notes
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        );
    }, [
        permissions,
        group,
        showGroupNotes,
        treeData,
        handleToggle,
        toggleState,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.GroupNotesView}
            loading={loadingGroup}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<GroupNotePageProps> => {
    let group: Dtos.Group | undefined = !(
        state.groups.formData instanceof Array
    )
        ? state.groups.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        group: group!,
        loadingGroup:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Pending,
        loadGroupSuccess:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Success,
        loadGroupFailure:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Failure,
        loadGroupErrorCode: state.groups.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (dispatch): Partial<GroupNotePageProps> => {
    return {
        loadGroup: bindActionCreators(GroupActions.LoadGroupById, dispatch),
        clearGroup: bindActionCreators(GroupActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupNotePage);
